<template>
  <div class="not-found-container">
    <div class="not-found-content">
      <!-- Animated 404 -->
      <div class="error-code">
        <span class="digit">4</span>
        <span class="digit middle">0</span>
        <span class="digit">4</span>
      </div>

      <!-- Error Message -->
      <h1 class="error-title">Page Not Found</h1>
      <p class="error-description">
        The page you're looking for doesn't exist or has been moved.
      </p>

      <!-- Helpful Actions -->
      <div class="action-buttons">
        <button @click="goHome" class="btn-primary">
          <i class="bi bi-house-door-fill me-2"></i>
          Go Home
        </button>
        <button @click="goBack" class="btn-secondary">
          <i class="bi bi-arrow-left me-2"></i>
          Go Back
        </button>
      </div>

      <!-- Helpful Links -->
      <div class="helpful-links">
        <p class="links-title">You might be looking for:</p>
        <div class="links-grid">
          <router-link to="/" class="link-card">
            <i class="bi bi-house-door"></i>
            <span>Dashboard</span>
          </router-link>
          <router-link to="/instances" class="link-card">
            <i class="bi bi-phone"></i>
            <span>Instances</span>
          </router-link>
          <router-link to="/warming/rooms" class="link-card">
            <i class="bi bi-door-open"></i>
            <span>Warming Rooms</span>
          </router-link>
          <router-link to="/warming/scripts" class="link-card">
            <i class="bi bi-file-text"></i>
            <span>Scripts</span>
          </router-link>
        </div>
      </div>

      <!-- Decorative Elements -->
      <div class="floating-shapes">
        <div class="shape shape-1"></div>
        <div class="shape shape-2"></div>
        <div class="shape shape-3"></div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'NotFound',
  methods: {
    goHome() {
      this.$router.push('/');
    },
    goBack() {
      this.$router.go(-1);
    }
  }
};
</script>

<style scoped>
.not-found-container {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #ffffff;
  position: relative;
  overflow: hidden;
  padding: 40px 20px;
}

.not-found-content {
  text-align: center;
  position: relative;
  z-index: 10;
  max-width: 700px;
  width: 100%;
  margin: 0 auto;
  margin-left: 670px; /* Shift right for sidebar (300px sidebar + 20px spacing) */
}

/* Animated 404 */
.error-code {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 15px;
  margin-bottom: 30px;
}

.digit {
  font-size: 140px;
  font-weight: 800;
  color: #014770;
  text-shadow: 0 4px 20px rgba(1, 71, 112, 0.15);
  animation: bounce 2s ease-in-out infinite;
  line-height: 1;
}

.digit.middle {
  animation-delay: 0.2s;
  color: #0489d6;
  font-size: 160px;
}

.digit:last-child {
  animation-delay: 0.4s;
}

@keyframes bounce {
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-20px);
  }
}

/* Error Text */
.error-title {
  font-size: 2.5rem;
  font-weight: 700;
  color: #2c3e50;
  margin-bottom: 15px;
}

.error-description {
  font-size: 1.1rem;
  color: #6c757d;
  margin-bottom: 40px;
  line-height: 1.6;
}

/* Action Buttons */
.action-buttons {
  display: flex;
  gap: 15px;
  justify-content: center;
  margin-bottom: 50px;
  flex-wrap: wrap;
}

.btn-primary,
.btn-secondary {
  padding: 14px 35px;
  border: none;
  border-radius: 50px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: inline-flex;
  align-items: center;
  box-shadow: 0 4px 15px rgba(1, 71, 112, 0.15);
}

.btn-primary {
  background: linear-gradient(135deg, #014770 0%, #0489d6 100%);
  color: white;
}

.btn-primary:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(1, 71, 112, 0.25);
  background: linear-gradient(135deg, #013a5c 0%, #0376b8 100%);
}

.btn-secondary {
  background: white;
  color: #014770;
  border: 2px solid #014770;
}

.btn-secondary:hover {
  background: #f8f9fa;
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(1, 71, 112, 0.15);
}

/* Helpful Links */
.helpful-links {
  margin-top: 40px;
}

.links-title {
  color: #6c757d;
  font-size: 1rem;
  margin-bottom: 20px;
  font-weight: 500;
}

.links-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(130px, 1fr));
  gap: 15px;
  max-width: 550px;
  margin: 0 auto;
}

.link-card {
  background: white;
  padding: 22px 15px;
  border-radius: 12px;
  text-decoration: none;
  color: #014770;
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  border: 2px solid #e9ecef;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.link-card:hover {
  background: linear-gradient(135deg, #014770 0%, #0489d6 100%);
  color: white;
  transform: translateY(-5px);
  box-shadow: 0 8px 20px rgba(1, 71, 112, 0.2);
  border-color: transparent;
}

.link-card i {
  font-size: 1.8rem;
}

.link-card span {
  font-size: 0.9rem;
  font-weight: 500;
}

/* Floating Shapes */
.floating-shapes {
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  pointer-events: none;
}

.shape {
  position: absolute;
  border-radius: 50%;
  background: linear-gradient(135deg, rgba(1, 71, 112, 0.08) 0%, rgba(4, 137, 214, 0.08) 100%);
  animation: float 20s ease-in-out infinite;
}

.shape-1 {
  width: 350px;
  height: 350px;
  top: 5%;
  left: 5%;
  animation-delay: 0s;
}

.shape-2 {
  width: 250px;
  height: 250px;
  bottom: 10%;
  right: 10%;
  animation-delay: 5s;
}

.shape-3 {
  width: 180px;
  height: 180px;
  top: 45%;
  right: 15%;
  animation-delay: 10s;
}

@keyframes float {
  0%, 100% {
    transform: translate(0, 0) rotate(0deg);
  }
  33% {
    transform: translate(30px, -30px) rotate(120deg);
  }
  66% {
    transform: translate(-20px, 20px) rotate(240deg);
  }
}

/* Responsive */
@media (max-width: 768px) {
  .not-found-container {
    padding: 30px 15px;
  }

  .not-found-content {
    margin-left: 0; /* Reset margin on mobile */
  }

  .digit {
    font-size: 90px;
  }

  .digit.middle {
    font-size: 110px;
  }

  .error-title {
    font-size: 1.8rem;
  }

  .error-description {
    font-size: 1rem;
  }

  .action-buttons {
    flex-direction: column;
    align-items: center;
  }

  .btn-primary,
  .btn-secondary {
    width: 100%;
    max-width: 280px;
    justify-content: center;
  }

  .links-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
  }

  .link-card {
    padding: 18px 12px;
  }

  .link-card i {
    font-size: 1.5rem;
  }

  .link-card span {
    font-size: 0.8rem;
  }
}

@media (max-width: 480px) {
  .digit {
    font-size: 70px;
  }

  .digit.middle {
    font-size: 90px;
  }

  .error-code {
    gap: 10px;
  }
}
</style>
